"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { HomeIcon, SearchIcon, RssIcon, GiftIcon, UserIcon } from "./icons";

type NavItem = {
  href: string;
  label: string;
  icon: typeof HomeIcon;
};

// Five real, existing routes - matches the brand spec's mobile nav exactly
// (Home / Search / Live Feed / Donate / Profile).
const ITEMS: NavItem[] = [
  { href: "/", label: "Home", icon: HomeIcon },
  { href: "/search", label: "Search", icon: SearchIcon },
  { href: "/transactions/live", label: "Live Feed", icon: RssIcon },
  { href: "/donate", label: "Donate", icon: GiftIcon },
  { href: "/profile", label: "Profile", icon: UserIcon },
];

function isActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

/**
 * Fixed mobile bottom navigation bar, visible below the `sm` breakpoint
 * only. Measures its own rendered height (including the iOS safe-area
 * inset) and publishes it as the `--site-bottom-nav-height` CSS variable,
 * the same pattern Navbar.tsx uses for `--site-nav-height`. app/components/
 * Chrome.tsx reads that variable to reserve bottom padding on page content -
 * previously that padding was a hardcoded guess (Tailwind's `pb-16`, 4rem)
 * which was shorter than this bar's actual height, so the bottom of every
 * page's content sat underneath the floating pill on mobile.
 */
export default function MobileBottomNav() {
  const pathname = usePathname() || "/";
  const navRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const element = navRef.current;
    if (!element) return;

    const root = document.documentElement;
    const updateHeight = () => {
      root.style.setProperty("--site-bottom-nav-height", `${Math.ceil(element.getBoundingClientRect().height)}px`);
    };

    updateHeight();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(updateHeight) : null;
    observer?.observe(element);
    window.addEventListener("resize", updateHeight);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateHeight);
      root.style.removeProperty("--site-bottom-nav-height");
    };
  }, []);

  // Auto-hide on scroll down, reveal on scroll up.
  //
  // Client report: "the bottom tool bar is fixed and overlaps". It does: this
  // is a floating pill roughly 110px tall on a 812px phone, so it permanently
  // covers the bottom ~14% of every page. On /search it was already sitting on
  // top of body copy at scroll position zero. Reserved padding fixes the END
  // of the page; it cannot help content the bar covers while you read.
  //
  // Hiding it as you scroll down (reading) and bringing it back as soon as you
  // scroll up (navigating) is the standard resolution and keeps it one gesture
  // away. Transform, not display/visibility, so the element keeps its layout
  // box and the ResizeObserver above keeps reporting a stable height - the
  // shell's reserved padding must not collapse or the page jumps.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const element = navRef.current;
    if (!element) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    let lastY = window.scrollY;
    let raf = 0;

    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY;
        // Ignore jitter and rubber-banding at either end.
        if (Math.abs(delta) < 6) return;
        const nearTop = y < 80;
        const nearBottom = y + window.innerHeight >= document.documentElement.scrollHeight - 80;
        const hide = delta > 0 && !nearTop && !nearBottom;
        element.dataset.hidden = hide ? "true" : "false";
        lastY = y;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      delete element.dataset.hidden;
    };
  }, []);

  return (
    <nav
      ref={navRef}
      aria-label="Primary mobile navigation"
      /* Styling hook for Chrome.tsx's html[data-kbd-open] rule, which hides
         this bar while the on-screen keyboard is up (FIX-58). */
      data-site-bottom-nav=""
      /* pointer-events-none on the wrapper, auto on the card: the <nav> is a
         full-width fixed box whose padding is transparent, so without this it
         intercepted taps across the whole bottom strip - including on controls
         that were visibly clear of the nav card itself. */
      className="sm:hidden pointer-events-none fixed inset-x-0 bottom-0 z-50 px-3 pb-[calc(env(safe-area-inset-bottom)+0.75rem)]"
    >
      <div
        className="pointer-events-auto mx-auto max-w-sm overflow-hidden rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,rgba(6,18,45,0.92),rgba(3,8,23,0.96))] px-2 pt-2 shadow-[0_24px_70px_-34px_rgba(0,0,0,0.98),0_12px_28px_-24px_rgba(20,124,255,0.55)] backdrop-blur-2xl"
        style={{
          backdropFilter: "blur(24px)",
          WebkitBackdropFilter: "blur(24px)",
        }}
      >
        <div className="mb-2 flex items-center justify-between px-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-400">Reqrium Mobile</span>
          <span className="inline-flex items-center gap-1 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-2 py-1 text-[10px] font-semibold text-cyan-100">
            <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" />
            Live
          </span>
        </div>
        <ul className="grid grid-cols-5 gap-1 pb-1">
          {ITEMS.map((item) => {
            const Icon = item.icon;
            const active = isActive(pathname, item.href);
            return (
              <li key={item.href} className="flex">
                <Link
                  href={item.href}
                  aria-current={active ? "page" : undefined}
                  className={`flex w-full flex-col items-center gap-1 rounded-2xl px-1.5 py-2.5 text-[10px] font-semibold transition-all ${
                    active
                      ? "bg-[linear-gradient(180deg,rgba(0,212,255,0.18),rgba(20,124,255,0.12))] text-cyan-100 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]"
                      : "text-slate-400"
                  }`}
                >
                  <span
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-full border ${
                      active
                        ? "border-cyan-400/30 bg-cyan-400/10 text-cyan-100"
                        : "border-white/8 bg-white/[0.03] text-slate-400"
                    }`}
                  >
                    <Icon size={18} strokeWidth={active ? 2.1 : 1.85} />
                  </span>
                  <span className="leading-none">{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
}
